/**
 * CTA deeplink helper.
 *
 * Most native apps (Instagram, Facebook, TikTok, YouTube, LinkedIn, Google Maps)
 * expose custom URL schemes that open the app directly instead of the mobile
 * browser. When a scheme is known, we try it first on mobile; if the app is not
 * installed, we fall back to the regular web URL so the user is never stuck.
 *
 * On desktop we always use the web URL (deeplinks rarely resolve).
 */

function extractSegment(url, pattern) {
  try {
    const m = url.match(pattern);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

// Build a deeplink URL for a given CTA.
// Returns null if no known deeplink scheme applies.
export function buildDeeplink(cta) {
  if (!cta || !cta.type) return null;
  const url = cta.url || "";
  const type = cta.type;

  switch (type) {
    case "app_download": {
      if (isIOS() && cta.ios_url) {
        // itms-apps:// opens App Store app directly; accepts the same path as https.
        const m = cta.ios_url.match(/apps\.apple\.com\/(.+)$/i);
        return m ? `itms-apps://apps.apple.com/${m[1]}` : null;
      }
      if (isAndroid() && cta.android_url) {
        // market:// opens Play Store app. Works with ?id=<pkg> form.
        const m = cta.android_url.match(/[?&]id=([^&]+)/i);
        return m ? `market://details?id=${m[1]}` : null;
      }
      return null;
    }
    case "instagram_follow": {
      const user = extractSegment(url, /instagram\.com\/([^/?#]+)/i);
      return user
        ? `instagram://user?username=${user}`
        : `instagram://app`;
    }
    case "instagram_story":
      return `instagram://story-camera`;
    case "facebook_like": {
      // fb://facewebmodal/f?href=<url> opens the FB app in-app browser to the page
      return `fb://facewebmodal/f?href=${encodeURIComponent(url)}`;
    }
    case "tiktok_follow": {
      const user = extractSegment(url, /tiktok\.com\/@?([^/?#]+)/i);
      return user ? `snssdk1233://user/profile?unique_id=${user}` : null;
    }
    case "youtube_subscribe": {
      // vnd.youtube://<path> opens the YouTube app
      const path = url.replace(/^https?:\/\/(www\.)?youtube\.com\//i, "");
      return path ? `vnd.youtube://${path}` : null;
    }
    case "linkedin_follow": {
      const seg = url.match(/linkedin\.com\/(company|in|school)\/([^/?#]+)/i);
      if (!seg) return null;
      return `linkedin://${seg[1]}/${seg[2]}`;
    }
    case "google_review": {
      // Google Maps app handles https://search.google.com/... and place URLs
      // via universal links — keep the web URL.
      return null;
    }
    case "tripadvisor_review": {
      return `tripadvisor://?url=${encodeURIComponent(url)}`;
    }
    case "app_download":
    case "newsletter_signup":
    case "account_creation":
    default:
      return null;
  }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

// Resolve the appropriate URL for the current device. Used mainly by the
// app_download CTA where the admin configures separate iOS / Android links.
function resolveWebUrl(cta) {
  if (cta.type === "app_download") {
    if (isIOS() && cta.ios_url) return cta.ios_url;
    if (isAndroid() && cta.android_url) return cta.android_url;
    return cta.url || cta.ios_url || cta.android_url || "";
  }
  return cta.url || "";
}

// Open the CTA in the best way available:
//  - For app_download: pick the iOS / Android URL based on the device, try
//    the native store deeplink first, then fall back to the web URL.
//  - For other types: try the scheme-based deeplink on mobile, with a web
//    fallback after 1.4s if the app isn't installed.
export function openCtaLink(cta) {
  if (!cta) return;
  const webUrl = resolveWebUrl(cta);
  if (!webUrl && !buildDeeplink(cta)) return;
  const deeplink = buildDeeplink(cta);

  if (!deeplink || !isMobileDevice()) {
    if (webUrl) window.open(webUrl, "_blank", "noopener,noreferrer");
    return;
  }

  let switched = false;
  const onHide = () => {
    if (document.hidden) switched = true;
  };
  const onLeave = () => {
    switched = true;
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onLeave);
  window.addEventListener("blur", onLeave);

  const a = document.createElement("a");
  a.href = deeplink;
  a.rel = "noopener noreferrer";
  a.style.position = "absolute";
  a.style.left = "-9999px";
  document.body.appendChild(a);
  try {
    a.click();
  } catch {
    // ignore
  }
  a.remove();

  setTimeout(() => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onLeave);
    window.removeEventListener("blur", onLeave);
    if (switched || document.hidden || !webUrl) return;
    // App not installed. Pop-ups outside the tap are blocked on iOS, so fall
    // back to this tab (the game progress is saved and restored on return).
    const w = window.open(webUrl, "_blank");
    if (w) w.opener = null;
    else window.location.assign(webUrl);
  }, 1400);
}
